import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ANALYTICS_GROUP_LABEL, ANALYTICS_GROUPS, NO_STRATEGY, type AnalyticsGroup, type AnalyticsRow } from '@vtf/shared';
import { api, errorText, getToken, refresh } from '../lib/api';
import { useMe } from '../lib/session';
import { fmtR, formatMoney, signClass } from '../lib/format';
import { Button, Card, CardHeader, cx, Empty, ErrorText, Field, Input, PageHeader, Segmented, Select, Stat } from '../components/ui';

/**
 * INT-DSH-04: performance analytics per trader, account, market and strategy for a period, with CSV export.
 * Closed trades only. The API decides what money the viewer may see (D8); this screen only shows what it receives.
 */

type Period = 'this_week' | 'last_week' | 'this_month' | 'custom';
interface Report {
  period: { fromDay: string; toDay: string; label: string; kind: Period; timezone: string };
  groupBy: AnalyticsGroup;
  currency: string;
  currencies: string[];
  rateDate: string | null;
  attribution: { text: string; url: string } | null;
  moneyScope: 'all' | 'own';
  accuracyApplies: boolean;
  rows: AnalyticsRow[];
  total: AnalyticsRow;
}
interface Options {
  traders: { id: string; name: string; status: string }[];
  accounts: { id: string; code: string; currency: string; market: string }[];
  markets: { id: string; code: string; name: string }[];
  strategies: { id: string; code: string; name: string }[];
}

const PERIODS: { value: Period; label: string }[] = [
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'this_month', label: 'This month' },
  { value: 'custom', label: 'Custom' },
];
const GROUPS = ANALYTICS_GROUPS.map((g) => ({ value: g, label: ANALYTICS_GROUP_LABEL[g] }));
const FILTERS = ['traderId', 'accountId', 'marketId', 'strategyId', 'currency'] as const;
/** Clicking a row narrows to it and looks one level deeper. */
const DRILL: Record<AnalyticsGroup, { filter: (typeof FILTERS)[number]; next: AnalyticsGroup }> = {
  trader: { filter: 'traderId', next: 'account' },
  account: { filter: 'accountId', next: 'strategy' },
  market: { filter: 'marketId', next: 'trader' },
  strategy: { filter: 'strategyId', next: 'trader' },
};

const pf = (v: string | null) => (v === null ? '—' : v === 'Infinity' ? '∞' : v);
const pct = (v: string | null) => (v === null ? '—' : `${v} %`);
const dd = (v: string | null) => (v === null ? '—' : Number(v) === 0 ? '0.00 R' : `−${v} R`);
const money = (v: string | null | undefined, ccy: string) => (v == null ? '—' : formatMoney(v, ccy, { signed: true }));
const rateDay = (d: string) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${d}T00:00:00Z`));

/** A label over a group of buttons (not a <label>: clicking it would press the first button). */
function Group({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-muted">{label}</span>
      {children}
    </div>
  );
}

/** Downloads the export with the in-memory bearer token (a plain link can't carry it). */
async function downloadCsv(query: string) {
  const go = () => fetch(`/api/v1/analytics/export?${query}`, { credentials: 'include', headers: { Authorization: `Bearer ${getToken() ?? ''}` } });
  let res = await go();
  if (res.status === 401 && (await refresh())) res = await go();
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string };
    throw new Error(body.message ?? `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'vtf-analytics.csv';
  const url = URL.createObjectURL(blob);
  const a = Object.assign(document.createElement('a'), { href: url, download: name });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function Accuracy({ a, applies }: { a: AnalyticsRow['accuracy']; applies: boolean }) {
  if (!a) return <span className="text-muted" title={applies ? 'Analyses are written per trader and market' : 'Not split by account or strategy'}>—</span>;
  if (!a.reviewed) return <span className="text-muted" title="No reviewed analyses in this period">no reviews</span>;
  return (
    <span className="flex flex-col">
      <span className="font-mono font-semibold">{a.biasPct} %</span>
      <span className="text-xs text-muted">
        {a.reviewed} reviewed{a.levelsPct !== null ? ` · levels ${a.levelsPct} %` : ''}
      </span>
    </span>
  );
}

export function Analytics() {
  const me = useMe();
  const tz = me.data?.timezone ?? 'UTC';
  const [params, setParams] = useSearchParams();
  const period = (params.get('period') as Period | null) ?? 'this_week';
  const groupBy = (params.get('groupBy') as AnalyticsGroup | null) ?? 'trader';
  const [custom, setCustom] = useState({ from: params.get('from') ?? '', to: params.get('to') ?? '' });
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const set = (patch: Record<string, string | null>) =>
    setParams((p) => {
      const n = new URLSearchParams(p);
      for (const [k, v] of Object.entries(patch)) {
        if (v) n.set(k, v);
        else n.delete(k);
      }
      return n;
    });

  const q = new URLSearchParams({ period, groupBy });
  if (period === 'custom') {
    q.set('from', params.get('from') ?? '');
    q.set('to', params.get('to') ?? '');
  }
  for (const k of FILTERS) if (params.get(k)) q.set(k, params.get(k)!);
  const qs = q.toString();
  const ready = period !== 'custom' || (!!params.get('from') && !!params.get('to'));

  const opts = useQuery({ queryKey: ['analytics-options'], queryFn: () => api<Options>('/analytics/options'), staleTime: 300_000 });
  const rep = useQuery({ queryKey: ['analytics', qs], queryFn: () => api<Report>(`/analytics?${qs}`), enabled: ready });
  const r = rep.data;
  const t = r?.total;
  const filtered = FILTERS.some((k) => k !== 'currency' && params.get(k));

  const exportCsv = async () => {
    setExporting(true);
    setExportErr(null);
    try {
      await downloadCsv(qs);
    } catch (e) {
      setExportErr(errorText(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Analytics"
        sub={r ? `${r.period.label} · closed trades · days in ${tz}` : 'How closed trades performed, per trader, account, market and strategy'}
        right={
          <Button variant="primary" onClick={() => void exportCsv()} disabled={!ready || exporting}>
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />

      <Card className="flex flex-col gap-3 p-3.5">
        <div className="flex flex-wrap items-end gap-3">
          <Group label="Period">
            <Segmented label="Period" options={PERIODS} value={period} onChange={(v) => set({ period: v === 'this_week' ? null : v, ...(v !== 'custom' ? { from: null, to: null } : {}) })} />
          </Group>
          {period === 'custom' && (
            <div className="flex flex-wrap items-end gap-2">
              <Field label="From">
                <Input type="date" value={custom.from} onChange={(e) => setCustom({ ...custom, from: e.target.value })} />
              </Field>
              <Field label="To">
                <Input type="date" value={custom.to} onChange={(e) => setCustom({ ...custom, to: e.target.value })} />
              </Field>
              <Button variant="primary" onClick={() => set({ from: custom.from, to: custom.to })} disabled={!custom.from || !custom.to}>
                Show
              </Button>
            </div>
          )}
        </div>
        <Group label="Group by">
          <Segmented label="Group by" options={GROUPS} value={groupBy} onChange={(v) => set({ groupBy: v === 'trader' ? null : v })} />
        </Group>
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="Trader">
            <Select value={params.get('traderId') ?? ''} onChange={(e) => set({ traderId: e.target.value })}>
              <option value="">All traders</option>
              {opts.data?.traders.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                  {x.id === me.data?.id ? ' (you)' : ''}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Account">
            <Select value={params.get('accountId') ?? ''} onChange={(e) => set({ accountId: e.target.value })}>
              <option value="">All accounts</option>
              {opts.data?.accounts.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.market}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Market">
            <Select value={params.get('marketId') ?? ''} onChange={(e) => set({ marketId: e.target.value })}>
              <option value="">All markets</option>
              {opts.data?.markets.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Strategy">
            <Select value={params.get('strategyId') ?? ''} onChange={(e) => set({ strategyId: e.target.value })}>
              <option value="">All strategies</option>
              {opts.data?.strategies.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.id === NO_STRATEGY ? 'No strategy tagged' : `${x.code} · ${x.name}`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Money in">
            <Select value={r?.currency ?? params.get('currency') ?? ''} onChange={(e) => set({ currency: e.target.value })}>
              {(r?.currencies ?? []).map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Field>
        </div>
        {filtered && (
          <Button variant="ghost" className="self-start" onClick={() => set({ traderId: null, accountId: null, marketId: null, strategyId: null })}>
            Clear filters
          </Button>
        )}
      </Card>
      <ErrorText error={rep.error ? errorText(rep.error) : exportErr} />

      {t && r && (
        <section aria-label="Totals" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Closed trades" value={t.metrics.trades} note={`${t.metrics.wins} won · ${t.metrics.losses} lost · ${t.metrics.breakevens} even`} />
          <Stat label="Win rate" value={pct(t.metrics.winRate)} note="share of closed trades won" />
          <Stat label="Average R" value={fmtR(t.metrics.avgR)} tone={signClass(t.metrics.avgR)} note={`over ${t.metrics.rTrades} trade${t.metrics.rTrades === 1 ? '' : 's'} with a risk`} />
          <Stat label="Expectancy" value={fmtR(t.metrics.expectancyR)} tone={signClass(t.metrics.expectancyR)} note="per trade, from wins and losses" />
          <Stat label="Profit factor" value={pf(t.metrics.profitFactorR)} note="R won / R lost" />
          <Stat label="Max drawdown" value={dd(t.metrics.maxDrawdownR)} tone={t.metrics.maxDrawdownR && Number(t.metrics.maxDrawdownR) > 0 ? 'text-down' : ''} note="worst fall of cumulative R" />
          {t.money ? (
            <Stat label={`Net P&L · ${r.currency}`} value={money(t.money.net, r.currency)} tone={signClass(t.money.net)} note={r.rateDate ? `converted at rates of ${rateDay(r.rateDate)}` : 'no conversion needed'} />
          ) : (
            <Stat label="Net P&L" value="—" note={t.metrics.trades ? 'includes other traders: R and % only' : 'no closed trades'} />
          )}
          <Stat label="Analysis accuracy" value={t.accuracy?.biasPct ? `${t.accuracy.biasPct} %` : '—'} note={t.accuracy ? `bias right · ${t.accuracy.reviewed} symbol${t.accuracy.reviewed === 1 ? '' : 's'} reviewed` : 'not split by account or strategy'} />
        </section>
      )}

      <Card>
        <CardHeader
          title={
            <>
              By {r ? ANALYTICS_GROUP_LABEL[r.groupBy].toLowerCase() : groupBy}
              {r && <span className="font-normal text-muted"> · {r.rows.length} rows</span>}
            </>
          }
          right={r && r.moneyScope === 'own' && <span className="text-xs text-muted">Money shows for your own trades only; others in R and %</span>}
        />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="sticky left-0 z-[1] bg-panel px-4 py-2.5 font-medium">{ANALYTICS_GROUP_LABEL[r?.groupBy ?? groupBy]}</th>
                {['Trades', 'Win rate', 'Avg R', 'Expectancy', 'Profit factor', 'Max DD', 'Total R'].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-right font-medium">
                    {h}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-right font-medium">Net P&amp;L{r ? ` · ${r.currency}` : ''}</th>
                <th className="px-3 py-2.5 font-medium">Analysis accuracy</th>
              </tr>
            </thead>
            <tbody>
              {r && [...r.rows, r.total].map((row, i) => {
                const isTotal = i === r.rows.length;
                const m = row.metrics;
                const drill = DRILL[r.groupBy];
                return (
                  <tr key={isTotal ? 'total' : `${row.key ?? 'x'}-${row.label}`} className={cx('border-t border-[#1a1f27]', isTotal && 'border-t-line2 bg-[#141a24] font-semibold')}>
                    <td className={cx('sticky left-0 z-[1] px-4 py-2.5 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.6)] sm:shadow-none', isTotal ? 'bg-[#141a24]' : 'bg-panel')}>
                      {!isTotal && row.key ? (
                        <button type="button" className="cursor-pointer text-left font-semibold text-accent" title={`Show ${row.label} by ${ANALYTICS_GROUP_LABEL[drill.next].toLowerCase()}`} onClick={() => set({ [drill.filter]: row.key, groupBy: drill.next })}>
                          {row.label}
                        </button>
                      ) : (
                        <span className="font-semibold">{row.label}</span>
                      )}
                      {row.sub && <span className="block text-xs font-normal text-muted">{row.sub}</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{m.trades}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{pct(m.winRate)}</td>
                    <td className={cx('px-3 py-2.5 text-right font-mono', signClass(m.avgR))}>{fmtR(m.avgR)}</td>
                    <td className={cx('px-3 py-2.5 text-right font-mono', signClass(m.expectancyR))}>{fmtR(m.expectancyR)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{pf(m.profitFactorR)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{dd(m.maxDrawdownR)}</td>
                    <td className={cx('px-3 py-2.5 text-right font-mono', signClass(m.totalR))}>{fmtR(m.totalR)}</td>
                    <td className="px-3 py-2.5 text-right">
                      {row.money ? (
                        <span className="flex flex-col items-end">
                          <span className={cx('font-mono', signClass(row.money.net))}>{money(row.money.net, r.currency)}</span>
                          {row.money.excluded > 0 && <span className="text-xs font-normal text-warn">{row.money.excluded} without an FX rate left out</span>}
                        </span>
                      ) : m.trades > 0 ? (
                        <span className="text-xs font-normal text-muted">R and % only</span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-normal">
                      <Accuracy a={row.accuracy} applies={r.accuracyApplies} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rep.isLoading && <Empty>Loading…</Empty>}
          {!ready && <Empty>Pick a start and an end date, then press Show.</Empty>}
          {r && !r.rows.length && <Empty>No closed trades or reviewed analyses in this period.</Empty>}
        </div>
        {r?.attribution && (
          <p className="m-0 border-t border-line px-4 py-2.5 text-xs text-muted">
            Money converted into {r.currency} at the latest rates{r.rateDate ? ` (${rateDay(r.rateDate)})` : ''} ·{' '}
            <a href={r.attribution.url} target="_blank" rel="noopener noreferrer">
              {r.attribution.text}
            </a>
          </p>
        )}
      </Card>

      <details className="rounded-xl border border-line bg-panel p-4 text-sm text-ink2">
        <summary className="cursor-pointer text-[15px] font-semibold text-ink">How the figures are worked out</summary>
        <ul className="mt-3 flex flex-col gap-1.5 pl-5">
          <li>Only closed trades count, in the period their close falls in (days in your timezone).</li>
          <li><strong>Win rate</strong> = winning trades / closed trades. A trade with exactly zero net P&amp;L is breakeven; after fees a scratch trade is usually a small loss.</li>
          <li><strong>Average R</strong> = total R / trades with an R. Trades logged without a stop (no initial risk) have no R: they count in trades, win rate and money, but not in any R figure.</li>
          <li><strong>Expectancy</strong> = win share × average win R + loss share × average loss R — the R you can expect per trade.</li>
          <li><strong>Profit factor</strong> = R won / R lost (∞ with no losses).</li>
          <li><strong>Max drawdown</strong> = the biggest fall of cumulative R from a peak, trades in closing order, starting at 0 R.</li>
          <li><strong>Analysis accuracy</strong> = bias right (partly counts half) / symbols reviewed, from end-of-day reviews of analyses for trading days in the period. It is per trader and market, so it isn't shown by account or strategy.</li>
          <li>Money is in the currency you pick, at the latest FX rates. You see money for your own trades; Master Traders and Risk Managers see it for everyone.</li>
        </ul>
        <p className="mt-3 mb-0">
          Each strategy's page shows its own floor statistics once enough trades exist — see the <Link to="/library?type=STRATEGY">strategies in the library</Link>.
        </p>
      </details>
    </>
  );
}
